import { Fragment, memo, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { MATCH_STATUS } from '@zmedia/bm-soccer-ui/constants';
import { IMatch } from '@zmedia/bm-soccer-ui/interface';
import { ArrayUtils } from '@zmedia/bm-soccer-ui/utils';
import {
    getDateInTextDetail,
    getDiffDate,
    getDiffDay,
    reverseDate,
    unixToDate,
} from '@zmedia/bm-soccer-ui/utils/date';
import {
    Button,
    Element,
    IconButton,
    Section,
} from '@zmedia/bm-soccer-ui/components';
import { URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/modules/shared';
import { Matches } from '@zmedia/bm-soccer-ui/modules/match';
import { SelectRound } from './components';
import styles from './style.module.scss';

const FILTER_TYPE = {
    ROUND: 'Theo vòng',
    DATE: 'Theo ngày',
};

const filterArr = [
    {
        name: FILTER_TYPE.DATE,
    },
    {
        name: FILTER_TYPE.ROUND,
    },
];

export function TournamentMatches(props: any) {
    const defaultItems = props.items || [];
    const [filter, setFilter] = useState<string>(FILTER_TYPE.DATE);

    if (!defaultItems.length) {
        return null;
    }

    const renderFilter = () => {
        return filterArr.map((item, index: number) => {
            return (
                <Button
                    key={index}
                    className={cn(styles['filter-item'], {
                        [styles['is-active']]: item.name === filter,
                    })}
                    onClick={() => setFilter(item.name)}
                >
                    {item.name}
                </Button>
            );
        });
    };

    return (
        <Section>
            <Element className={styles['match-filter']}>
                {renderFilter()}
            </Element>
            {filter === FILTER_TYPE.ROUND ? (
                <MatchRound {...props} />
            ) : (
                <MatchDate {...props} />
            )}
        </Section>
    );
}

function MatchRound(props: any) {
    const defaultItems: IMatch[] = props.items || [];
    let defaultRound = props.currentRound || 1;
    if (defaultItems[0].status.id === MATCH_STATUS.NOT_STARTED) {
        defaultRound = 1;
    }
    const isPastTournament =
        getDiffDay(defaultItems[defaultItems.length - 1].startTime) >= 0;

    let isGroup = false;
    if (defaultItems[0]?.group?.id) {
        isGroup = true;
    }

    const groupRound = ArrayUtils.groupByRound(defaultItems, isGroup);
    const nameRounds = Object.keys(groupRound);
    const groupStage = nameRounds.filter((name: string) => {
        return name.indexOf('Bảng') > -1;
    });

    const totalRound = nameRounds.length;
    if (nameRounds.length > groupStage.length) {
        defaultRound = defaultRound + groupStage.length;
    }

    const router = useRouter();
    const round = +(router.query.round || '');

    let currentRound = round > 0 && round <= totalRound ? round : defaultRound;

    const items = ArrayUtils.groupByDate(
        groupRound[nameRounds[currentRound - 1]]
    );

    const renderItem = (item: any, index: number) => {
        return (
            <Fragment key={index}>
                <Element className={styles['group-header']}>{item[0]}</Element>
                <Matches matches={item[1]} isGroup={isGroup} />
            </Fragment>
        );
    };

    const renderItems = (items: any) => {
        return Object.entries(items)?.map((item, index) => {
            return renderItem(item, index);
        });
    };

    const goPrevRound = () => {
        const prevRound = +currentRound - 1;
        const seasonId = router.query.seasonId;
        router.push(
            `${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${
                props.path
            }?round=${prevRound}${seasonId ? `&seasonId=${seasonId}` : ''}`
        );
    };

    const goNextRound = () => {
        const nextRound = +currentRound + 1;
        const seasonId = router.query.seasonId;
        router.push(
            `${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${
                props.path
            }?round=${nextRound}${seasonId ? `&seasonId=${seasonId}` : ''}`
        );
    };

    return (
        <>
            <Element className={styles['match-tournament-header']}>
                <IconButton
                    className={currentRound <= 1 ? 'is-hidden' : ''}
                    icon="back-2"
                    onClick={goPrevRound}
                    title="Trước"
                />
                <SelectRound
                    listRound={nameRounds}
                    currentRound={currentRound}
                    isReverse={isPastTournament}
                    url={`${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${props.path}`}
                />
                <IconButton
                    className={currentRound == totalRound ? 'is-hidden' : ''}
                    icon="forward-2"
                    title="Sau"
                    onClick={goNextRound}
                />
            </Element>
            <Element className={styles['match-group']}>
                {renderItems(items)}
            </Element>
        </>
    );
}

function MatchDate(props: any) {
    const defaultItems = props.items || [];
    const groupDate = ArrayUtils.groupByDate(defaultItems);
    const router = useRouter();
    const page = +(router.query.page || '');
    const isPastTournament =
        getDiffDay(defaultItems[defaultItems.length - 1].startTime) >= 0;

    const matchByDate = ArrayUtils.splitArr(Object.entries(groupDate));

    const totalPage = matchByDate.length;

    const _defaultDate = unixToDate(
        defaultItems.filter((item: IMatch) => {
            const diff = getDiffDay(item.startTime);
            return diff <= 0;
        })[0]?.startTime,
        'DD/MM/YYYY'
    );

    let defaultIndex = 0;

    if (isPastTournament || _defaultDate) {
        matchByDate.forEach((items: any[], index: number) => {
            items.forEach((item: any) => {
                if (item[0].indexOf(_defaultDate) > -1) {
                    defaultIndex = index;
                    return;
                }
            });
        });
    }

    const defaultPage = defaultIndex + 1;
    const currentPage = page > 0 && page <= totalPage ? page : defaultPage;

    const renderItem = (item: any, index: number) => {
        return (
            <Fragment key={index}>
                <Element className={styles['group-header']}>{item[0]} </Element>
                <Matches matches={item[1]} renderRoundInDate={true} />
            </Fragment>
        );
    };

    const renderItems = (items: any[] = []) => {
        return items.map((item, index) => {
            return renderItem(item, index);
        });
    };

    const goPrevDate = () => {
        const prevPage = +currentPage - 1;
        const seasonId = router.query.seasonId;
        const url = `${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${
            props.path
        }?page=${prevPage}${seasonId ? `&seasonId=${seasonId}` : ''}`;
        router.push(url);
    };

    const goNextDate = () => {
        const nextPage = +currentPage + 1;
        const seasonId = router.query.seasonId;
        const url = `${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${
            props.path
        }?page=${nextPage}${seasonId ? `&seasonId=${seasonId}` : ''}`;
        router.push(url);
    };

    // lay startTime cua match theo group
    const currentMatchByDate = matchByDate[currentPage - 1];
    const startDate = currentMatchByDate?.[0]?.[0];
    const endDate = currentMatchByDate?.[currentMatchByDate.length - 1]?.[0];
    const startDateText = getDateInTextDetail(
        reverseDate(startDate.split(',')[1]),
        'DD/MM/YYYY'
    );

    const endDateText = endDate
        ? ` - ${getDateInTextDetail(
              reverseDate(endDate.split(',')[1]),
              'DD/MM/YYYY'
          )}`
        : '';

    const isDiffStart = getDiffDate(startDate, 'dddd, DD/MM/YYYY');
    const isDiffEnd = getDiffDate(endDate, 'dddd, DD/MM/YYYY');

    const isReverseMatch = isDiffStart >= 0 && isDiffEnd >= 0;

    return (
        <>
            <Element className={styles['match-tournament-header']}>
                <IconButton
                    className={currentPage <= 1 ? 'is-hidden' : ''}
                    icon="back-2"
                    title="Trước"
                    onClick={goPrevDate}
                />
                <Element className={styles['select-date']}>
                    {startDateText}
                    {endDateText}
                </Element>
                <IconButton
                    className={currentPage == totalPage ? 'is-hidden' : ''}
                    icon="forward-2"
                    title="Sau"
                    onClick={goNextDate}
                />
            </Element>
            <Element className={styles['match-group']}>
                {renderItems(
                    isReverseMatch
                        ? matchByDate[currentPage - 1].reverse()
                        : matchByDate[currentPage - 1]
                )}
            </Element>
        </>
    );
}

export default memo(TournamentMatches);
